/**
 * src/services/mediaDevices.ts
 *
 * Thin helpers around `navigator.mediaDevices` for the live-broadcast
 * flow.  Centralising this keeps ProfileOverlay focused on UX — device
 * enumeration, permission probing, facing-mode selection, and capture
 * error normalisation all live here.
 *
 * Key idea: vendors differ wildly on what `getUserMedia` throws when
 * no camera exists (Tesla's infotainment browser, desktops without a
 * webcam, kiosk hardware, etc.).  The normalised `CaptureErrorKind`
 * lets the UI render consistent guidance regardless of platform.
 */

export interface VideoDeviceInfo {
  deviceId: string;
  label: string;
  /** Best-effort guess at facing direction inferred from the label. */
  facing: 'user' | 'environment' | 'unknown';
}

export type CaptureErrorKind =
  | 'no_camera_device'       // platform exposes zero cameras (Tesla, kiosk)
  | 'camera_not_allowed'     // user denied permission
  | 'camera_in_use'          // another app/tab already holds the camera
  | 'insecure_origin'        // page served over http (needs https/localhost)
  | 'overconstrained'        // requested device/facingMode unavailable
  | 'unknown';

export interface NormalisedCaptureError {
  kind: CaptureErrorKind;
  message: string;
  cause?: unknown;
}

/** Heuristic mapping of camera label → facing direction.  The
 *  MediaDevices API does not expose this directly on desktop
 *  browsers, so we match against vendor naming conventions. */
function guessFacing(label: string): VideoDeviceInfo['facing'] {
  const l = (label || '').toLowerCase();
  if (/front|user|face|selfie/.test(l)) return 'user';
  if (/back|rear|environment|world|wide|telephoto|ultra/.test(l)) return 'environment';
  return 'unknown';
}

/**
 * Enumerate available video input devices.  Labels are only populated
 * after the user has granted camera permission at least once, so this
 * function silently probes `getUserMedia` before enumerating if the
 * labels come back empty.  The probe stream is released immediately.
 */
export async function listVideoDevices(): Promise<VideoDeviceInfo[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
  try {
    let devices = await navigator.mediaDevices.enumerateDevices();
    let videoInputs = devices.filter((d) => d.kind === 'videoinput');
    const needsProbe = videoInputs.length > 0 && videoInputs.every((d) => !d.label);
    if (needsProbe) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ video: true });
        probe.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
        devices = await navigator.mediaDevices.enumerateDevices();
        videoInputs = devices.filter((d) => d.kind === 'videoinput');
      } catch { /* labels stay empty — fine, UI falls back to generic names */ }
    }
    return videoInputs.map((d) => ({
      deviceId: d.deviceId,
      label: d.label || 'Camera',
      facing: guessFacing(d.label),
    }));
  } catch {
    return [];
  }
}

/**
 * Acquire a MediaStream with the requested camera preferences, giving
 * callers a consistent error type regardless of which browser/platform
 * threw.  Tesla's infotainment browser, desktops without webcams, and
 * kiosk hardware all map to `no_camera_device`.
 */
export async function captureStream(opts: {
  deviceId?: string;
  facing?: 'user' | 'environment';
  audio?: boolean;
} = {}): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw normaliseCaptureError(new Error('mediaDevices_unavailable'));
  }
  const videoConstraints: MediaTrackConstraints = {};
  if (opts.deviceId) videoConstraints.deviceId = { exact: opts.deviceId };
  else if (opts.facing) videoConstraints.facingMode = { ideal: opts.facing };
  const constraints: MediaStreamConstraints = {
    video: Object.keys(videoConstraints).length > 0 ? videoConstraints : true,
    audio: opts.audio !== false,
  };
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (err: any) {
    // If an `exact` deviceId was requested and the device vanished
    // (unplugged between enumerate and capture), retry without the
    // constraint — better to get *any* camera than outright fail.
    if (opts.deviceId && (err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError')) {
      try {
        return await navigator.mediaDevices.getUserMedia({ video: true, audio: opts.audio !== false });
      } catch (inner) {
        throw normaliseCaptureError(inner);
      }
    }
    throw normaliseCaptureError(err);
  }
}

/**
 * Translate a DOMException (or any thrown value) into a
 * UI-friendly `NormalisedCaptureError`.  Exported so callers that
 * use their own capture path (e.g. screen-share, file-picker) can
 * reuse the same taxonomy.
 */
export function normaliseCaptureError(err: unknown): NormalisedCaptureError {
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name || '';
  const msg  = e?.message || '';

  // Tesla/desktop-without-cam: no device exists at all.  Chrome uses
  // `NotFoundError`, Firefox sometimes uses `NotReadableError`.
  if (name === 'NotFoundError' || /not.?found/i.test(msg) || /requested device not found/i.test(msg)) {
    return { kind: 'no_camera_device', message: 'No camera detected on this device.', cause: err };
  }
  if (name === 'NotAllowedError' || /permission|denied/i.test(msg)) {
    return { kind: 'camera_not_allowed', message: 'Camera/microphone permission was denied.', cause: err };
  }
  if (name === 'NotReadableError' || /hardware|in use|could not start/i.test(msg)) {
    return { kind: 'camera_in_use', message: 'Camera is in use by another app.', cause: err };
  }
  if (name === 'OverconstrainedError' || /overconstrained/i.test(msg)) {
    return { kind: 'overconstrained', message: 'Selected camera is unavailable.', cause: err };
  }
  if (/secure|https/i.test(msg)) {
    return { kind: 'insecure_origin', message: 'Camera requires https:// (or localhost).', cause: err };
  }
  return { kind: 'unknown', message: msg || 'Failed to capture camera.', cause: err };
}

/**
 * Cycle helper: given the currently-selected device and a list of
 * available cameras, return the *next* device to switch to.  Prefers
 * cycling by facing direction when mixing front/rear on a phone.
 */
export function nextCameraDevice(
  current: VideoDeviceInfo | null,
  all: VideoDeviceInfo[]
): VideoDeviceInfo | null {
  if (all.length === 0) return null;
  if (all.length === 1) return all[0];
  if (!current) return all[0];
  // Prefer flipping facing direction when we know both sides.
  if (current.facing === 'user') {
    const env = all.find((d) => d.facing === 'environment');
    if (env) return env;
  } else if (current.facing === 'environment') {
    const user = all.find((d) => d.facing === 'user');
    if (user) return user;
  }
  const idx = all.findIndex((d) => d.deviceId === current.deviceId);
  return all[(idx + 1) % all.length];
}
